import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';

function formatVideoDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

type VideoThumbnailProps = {
  src: string;
  onPlay?: () => void;
};

export default function VideoThumbnail({ src, onPlay }: VideoThumbnailProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const syncDuration = () => setDuration(formatVideoDuration(video.duration));
    const showFrame = () => {
      if (video.duration > 0.2) {
        video.currentTime = 0.1;
      }
      setReady(true);
    };

    video.addEventListener('loadedmetadata', syncDuration);
    video.addEventListener('loadeddata', showFrame);
    if (video.readyState >= 2) showFrame();
    if (video.readyState >= 1) syncDuration();

    return () => {
      video.removeEventListener('loadedmetadata', syncDuration);
      video.removeEventListener('loadeddata', showFrame);
    };
  }, [src]);

  return (
    <div className="relative h-[120px] w-full shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 sm:w-[150px]">
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="metadata"
        className={`h-full w-full object-cover transition-opacity duration-300 ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div className="absolute inset-0 grid place-items-center bg-black/15">
        <button
          type="button"
          onClick={onPlay}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/95 text-emerald-700 shadow-md"
          aria-label="Play video"
        >
          <Play size={18} fill="currentColor" />
        </button>
      </div>
      {duration ? (
        <span className="absolute bottom-2.5 left-2.5 rounded-md bg-black/75 px-2 py-0.5 text-[11px] font-semibold text-white">
          {duration}
        </span>
      ) : null}
    </div>
  );
}
